import { useCallback, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";

import { signout } from "../../store/reducers/loginData";
import { resetFilters } from "../../store/reducers/filters";
import constants from "../../utils/constants";
import toastMessage from "../../utils/toastMessage";

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "touchstart",
  "scroll",
  "wheel",
];

const LAST_ACTIVITY_KEY = "lastActivityAt";
const CHECK_INTERVAL_MS = 15 * 1000;
const PERSIST_INTERVAL_MS = 5 * 1000;

const useIdleTimeout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const lastActivityRef = useRef(Date.now());
  const lastPersistedRef = useRef(0);
  const isExpiringRef = useRef(false);

  const expireSession = useCallback(async () => {
    // The interval can fire again while the signout below is still awaiting.
    if (isExpiringRef.current) {
      return;
    }
    isExpiringRef.current = true;

    dispatch(resetFilters());
    await dispatch(signout());
    navigate("/login", { replace: true });
    toastMessage(
      constants.TOAST_TYPE.ERROR,
      "You have been logged out due to inactivity. Please log in again."
    );
  }, [dispatch, navigate]);

  useEffect(() => {
    const storedActivity = Number(sessionStorage.getItem(LAST_ACTIVITY_KEY));
    lastActivityRef.current = storedActivity || Date.now();

    const markActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;

      // Events like mousemove fire constantly; the idle check does not need
      // more than a few seconds of accuracy.
      if (now - lastPersistedRef.current > PERSIST_INTERVAL_MS) {
        lastPersistedRef.current = now;
        sessionStorage.setItem(LAST_ACTIVITY_KEY, String(now));
      }
    };

    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, markActivity, { passive: true })
    );

    // Comparing timestamps rather than using a single setTimeout keeps the
    // logout accurate when the tab is backgrounded or the machine sleeps.
    const interval = setInterval(() => {
      if (
        Date.now() - lastActivityRef.current >=
        constants.SESSION_IDLE_TIMEOUT_MS
      ) {
        expireSession();
      }
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((event) =>
        window.removeEventListener(event, markActivity)
      );
      clearInterval(interval);
    };
  }, [expireSession]);
};

export default useIdleTimeout;
